import { SelectProductCollection } from '@components/feed/SelectProductCollection';
import SelectProductModal from '@components/product/SelectProductModal';
import { SelectUser } from '@components/product/SelectUser';
import SelectUserContentModal from '@components/user-contents/SelectUserContentModal';
import { DeeplinkAction, DeepLinkActions } from '@models/common';
import { FeedDataItem } from '@models/feed';
import { useQuery } from '@tanstack/react-query';
import { Button, Checkbox, Input, Select } from 'antd';
import React, { useEffect, useState } from 'react';
import * as noticeApi from '@apis/admin-notice';
import { Notice } from '@models/notice';
import * as feedApi from '@apis/feeds-v2';
import * as lastChanceApi from '@apis/last-chance';
import { ProductCollection } from '@models/productCollection';
import { LastChanceProductListItem } from '@models/last-chance';
import {
  buildLastChanceSelectOptions,
  getLastChanceContentTitle,
  lastChanceSelectDropdownProps,
} from '@utils/last-chance-select-option';

const LOUNGE_TAB_TO_BOARD: { [key: string]: string | undefined } = {
  all: undefined,
  vote: 'vote',
  review: 'review',
  news: 'partner-news',
  curation: 'curation',
};

const toPlainText = (value?: string) => {
  if (!value) {
    return '';
  }
  return value
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();
};

export const DEEPLINK_ACTION_LABELS: { [key: string]: any } = {
  [DeepLinkActions.GO_TO_MAIN]: { label: '홈으로 이동' },
  [DeepLinkActions.GO_TO_COLLECTION]: { label: '기획전으로 이동' },
  [DeepLinkActions.GO_TO_PRODUCT]: { label: '상품상세로 이동' },
  [DeepLinkActions.GO_TO_THEME_COLLECTION]: { label: '테마 기획전으로 이동' },
  [DeepLinkActions.SEARCH_KEYWORD]: { label: '검색하기' },
  [DeepLinkActions.GO_TO_COMMUNITY]: { label: '커뮤니티로 이동' },
  [DeepLinkActions.GO_TO_NOTICE]: { label: '공지사항으로 이동' },
  [DeepLinkActions.OPEN_LINK]: { label: '링크 열기' },
  [DeepLinkActions.OPEN_OS_BROWSER]: { label: 'OS 브라우저 열기' },
  [DeepLinkActions.GO_TO_COUPON]: { label: '쿠폰으로 이동' },
  [DeepLinkActions.GO_TO_CHAT]: { label: '채팅으로 이동' },
  [DeepLinkActions.GO_TO_CHAT_LIST]: { label: '채팅 목록으로 이동' },
  [DeepLinkActions.GO_TO_REWARD]: { label: '리워드 페이지로 이동' },
  [DeepLinkActions.GO_TO_LOUNGE]: { label: '라운지로 이동' },
  [DeepLinkActions.GO_TO_DEALS]: { label: '특가로 이동' },
  [DeepLinkActions.GO_TO_DEALS_DETAIL]: { label: '특가상품상세로 이동' },
  [DeepLinkActions.GO_TO_FAVORITES]: { label: '나의 찜 페이지로 이동' },
  [DeepLinkActions.GO_TO_PARTNER]: { label: '파트너 프로필로 이동' },
  [DeepLinkActions.GO_TO_SELF_ASSESSMENT]: { label: '다중지능검사로 이동' },
  [DeepLinkActions.GO_TO_MBTI_TEST]: { label: 'MBTI 테스트로 이동' },
  [DeepLinkActions.GO_TO_EVENT]: { label: '이벤트 페이지로 이동' },
};

export const DeeplinkActionEdit: React.FC<{
  data: Partial<FeedDataItem>;
  onChange: (actionData: DeeplinkAction) => void;
}> = (props) => {
  const [changedActionData, setChangedActionData] = useState<DeeplinkAction>();
  const [showProductSelect, setShowProductSelect] = useState<boolean>(false);
  const [showUserContentSelect, setShowUserContentSelect] = useState<boolean>(false);
  const [lastChanceSearchKeyword, setLastChanceSearchKeyword] = useState<string>('');
  const [lastChanceQuery, setLastChanceQuery] = useState<{ keyword?: string }>({});
  const action = changedActionData?.action || props.data.action;
  const payload = changedActionData?.payload || props.data.payload;
  const productTitle = changedActionData?.payload?.title || props.data.payload?.title;

  const noticeSearchQuery = useQuery({
    queryKey: [`admin-get-notices`],
    queryFn: () => noticeApi.searchNotices({ page: 0, size: 1000 }),
    enabled: action && action === DeepLinkActions.GO_TO_NOTICE,
  });
  const noticeSearchResult = noticeSearchQuery.data?.data.contents || [];
  const noticeSearchResultOptions = noticeSearchResult.map((item: Notice) => {
    return {
      value: item._id,
      label: `${item.title}`,
      children: item,
    };
  });

  const feedProductCollectionQuery = useQuery({
    queryKey: [`admin-feed-product-collections`],
    queryFn: () => feedApi.getProductCollectionFeed(),
    enabled:
      action && (action === DeepLinkActions.GO_TO_COLLECTION || action === DeepLinkActions.GO_TO_THEME_COLLECTION),
  });

  const lastChanceSearchQuery = useQuery({
    queryKey: [`admin-search-last-chance-products`, lastChanceQuery.keyword],
    queryFn: () => lastChanceApi.searchLastChanceProducts({ ...lastChanceQuery, page: 0, size: 500 }),
    enabled: action === DeepLinkActions.GO_TO_DEALS_DETAIL && !!lastChanceQuery.keyword,
  });
  const lastChanceSearchResult = lastChanceQuery.keyword ? lastChanceSearchQuery.data?.data.contents || [] : [];
  const lastChanceSearchResultOptions = buildLastChanceSelectOptions(lastChanceSearchResult);

  const collectionTapOptions = feedProductCollectionQuery.data?.data.data?.map((item: ProductCollection) => {
    return {
      value: item.type,
      label: `${item.type}`,
    };
  });

  useEffect(() => {
    setLastChanceSearchKeyword('');
    setLastChanceQuery({});
  }, [action]);

  useEffect(() => {
    if (changedActionData) {
      props.onChange(changedActionData);
    }
  }, [changedActionData]);

  useEffect(() => {
    if (!props.data.action && props.data.direction) {
      let action = undefined;
      let payload = undefined;
      if (
        props.data.direction === '통합 기획전' ||
        props.data.direction === '기획전' ||
        props.data.direction === '한정판매 기획전'
      ) {
        action = DeepLinkActions.GO_TO_COLLECTION;
        payload = { title: props.data.direction2_name, collectionId: props.data.direction2 };
      } else if (
        props.data.direction === '클래스 상세' ||
        props.data.direction === '상품상세' ||
        props.data.direction === '한정판매'
      ) {
        action = DeepLinkActions.GO_TO_PRODUCT;
        payload = { title: props.data.direction2_name, productId: props.data.direction2 };
      } else if (props.data.direction === '커뮤니티(리뷰)') {
        action = DeepLinkActions.GO_TO_COMMUNITY;
      } else if (props.data.direction === '직접 링크') {
        action = DeepLinkActions.OPEN_LINK;
        payload = { url: props.data.direction2 };
      } else if (props.data.direction === '검색어 직접 입력') {
        action = DeepLinkActions.SEARCH_KEYWORD;
        payload = { keyword: props.data.direction2 };
      }

      if (action) {
        setChangedActionData({ action, payload });
      }
    }
  }, [props.data]);

  return (
    <div>
      <SelectProductModal
        open={showProductSelect}
        onAddProducts={(products) => {
          setChangedActionData((prev) => {
            return { ...prev, payload: { productId: products[0]._id, title: products[0].title } };
          });
        }}
        onClose={() => {
          setShowProductSelect(false);
        }}></SelectProductModal>
      <SelectUserContentModal
        open={showUserContentSelect}
        selectLimit={1}
        board={LOUNGE_TAB_TO_BOARD[payload?.tab || ''] || 'all'}
        onAddUserContents={(contents) => {
          const content = contents[0];
          if (!content) {
            return;
          }
          const contentTitle = toPlainText(content.title) || toPlainText(content.text) || content._id;
          setChangedActionData((prev) => {
            return {
              ...prev,
              payload: {
                ...prev?.payload,
                userContentId: content._id,
                title: contentTitle,
              },
            };
          });
        }}
        onClose={() => {
          setShowUserContentSelect(false);
        }}
      />
      <Select
        style={{ width: 320 }}
        value={action}
        onChange={(value) => {
          setChangedActionData({
            action: value as DeepLinkActions,
            payload: {},
          });
        }}
        options={Object.keys(DEEPLINK_ACTION_LABELS).map((key) => {
          return { value: key, label: DEEPLINK_ACTION_LABELS[key].label };
        })}></Select>
      <div>
        {(action === DeepLinkActions.GO_TO_COLLECTION || action === DeepLinkActions.GO_TO_THEME_COLLECTION) && (
          <div>
            <div style={{ marginTop: '4px' }}>탭 선택(* 탭 or 특정 기획전 중 택일)</div>
            <Select
              style={{ width: 320 }}
              value={payload?.tab}
              placeholder={'탭 선택'}
              onChange={(value) => {
                setChangedActionData((prev) => {
                  return {
                    ...prev,
                    payload: { ...prev?.payload, tab: value, collectionId: undefined, title: undefined },
                  };
                });
              }}
              options={collectionTapOptions}></Select>
            <div>
              <div style={{ marginTop: '4px' }}>기획전 선택</div>
              <SelectProductCollection
                selectedId={payload?.collectionId}
                onSelect={(value) => {
                  setChangedActionData((prev) => {
                    return { ...prev, payload: { collectionId: value._id, title: value.title, tab: undefined } };
                  });
                }}></SelectProductCollection>
              <div style={{ marginTop: '8px' }}>
                <div>
                  <span>선택: </span>
                  {payload?.collectionId
                    ? `${productTitle} 기획전`
                    : payload?.tab
                      ? `기획전 - ${payload?.tab} 탭`
                      : '기획전 메인'}
                </div>
              </div>
            </div>
          </div>
        )}
        {action === DeepLinkActions.GO_TO_PRODUCT && (
          <div style={{ marginTop: '8px' }}>
            <Button
              onClick={() => {
                setShowProductSelect(true);
              }}>
              상품 선택하기
            </Button>
            <div style={{ marginTop: '8px' }}>
              <span>선택: </span>
              {productTitle}
            </div>
          </div>
        )}
        {action === DeepLinkActions.GO_TO_DEALS_DETAIL && (
          <div style={{ marginTop: '8px' }}>
            <div style={{ marginBottom: '5px' }}>
              검색:
              <Input
                value={lastChanceSearchKeyword}
                placeholder={'상품명 또는 옵션명을 입력해주세요'}
                onChange={(e) => {
                  setLastChanceSearchKeyword(e.target.value);
                }}
                onPressEnter={() => {
                  setLastChanceQuery({ keyword: lastChanceSearchKeyword });
                }}></Input>
            </div>
            {(lastChanceSearchResult.length > 0 || payload?.lastChanceItemId) && (
              <Select
                style={{ width: '100%' }}
                value={payload?.lastChanceItemId}
                placeholder={'특가상품 선택'}
                {...lastChanceSelectDropdownProps}
                dropdownStyle={{ minWidth: 520 }}
                onChange={(value, option: any) => {
                  const item = option.item as LastChanceProductListItem;
                  setChangedActionData((prev) => {
                    return {
                      ...prev,
                      payload: {
                        lastChanceItemId: value,
                        title: getLastChanceContentTitle(item),
                      },
                    };
                  });
                }}
                options={lastChanceSearchResultOptions}
              />
            )}
            <div style={{ marginTop: '8px' }}>
              <span>선택: </span>
              {payload?.title || payload?.lastChanceItemId || ''}
            </div>
          </div>
        )}
        {(action === DeepLinkActions.OPEN_LINK || action === DeepLinkActions.OPEN_OS_BROWSER) && (
          <div style={{ marginTop: '8px' }}>
            <Input
              value={payload?.url}
              onChange={(e) => {
                setChangedActionData((prev) => {
                  return { ...prev, payload: { url: e.target.value } };
                });
              }}></Input>
          </div>
        )}
        {action === DeepLinkActions.SEARCH_KEYWORD && (
          <div style={{ marginTop: '8px' }}>
            <Input
              value={payload?.keyword}
              onChange={(e) => {
                setChangedActionData((prev) => {
                  return { ...prev, payload: { keyword: e.target.value } };
                });
              }}></Input>
          </div>
        )}
        {action === DeepLinkActions.GO_TO_LOUNGE && (
          <div style={{ marginTop: '8px', display: 'flex', gap: '4px', flexDirection: 'column' }}>
            <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
              <Select
                style={{ width: 320 }}
                value={payload?.tab}
                onChange={(value) => {
                  setChangedActionData((prev) => {
                    return { ...prev, payload: { ...prev?.payload, tab: value } };
                  });
                }}
                options={[
                  { value: 'all', label: '홈' },
                  { value: 'vote', label: '투표' },
                  { value: 'review', label: '후기' },
                  { value: 'news', label: '소식' },
                  { value: 'curation', label: '콘텐츠발견' },
                ]}></Select>
              <Checkbox
                style={{ flexShrink: 0 }}
                checked={payload?.write}
                onChange={(e) => {
                  setChangedActionData((prev) => {
                    return { ...prev, payload: { ...prev?.payload, write: e.target.checked } };
                  });
                }}>
                글작성
              </Checkbox>
            </div>

            <div style={{ marginTop: '4px' }}>
              <Button
                onClick={() => {
                  setShowUserContentSelect(true);
                }}>
                콘텐츠 선택하기
              </Button>
              <div style={{ marginTop: '8px' }}>
                <span>선택: </span>
                {payload?.userContentId ? `${payload?.title || ''} (${payload.userContentId})` : ''}
              </div>
            </div>
          </div>
        )}
        {action === DeepLinkActions.GO_TO_DEALS && (
          <div style={{ marginTop: '8px' }}>
            <div style={{ marginTop: '4px' }}>탭 선택</div>
            <Select
              style={{ width: 320 }}
              value={payload?.tab}
              placeholder={'탭 선택'}
              onChange={(value) => {
                setChangedActionData((prev) => {
                  return { ...prev, payload: { ...prev?.payload, tab: value } };
                });
              }}
              options={[
                { value: 'home', label: '홈' },
                { value: 'last-chance', label: '라스트찬스' },
                { value: 'time-sale', label: '타임세일' },
              ]}></Select>
          </div>
        )}
        {action === DeepLinkActions.GO_TO_FAVORITES && (
          <div style={{ marginTop: '8px', display: 'flex', gap: '4px', flexDirection: 'column' }}>
            <Select
              style={{ width: 320 }}
              value={payload?.tab}
              onChange={(value) => {
                setChangedActionData((prev) => {
                  return { ...prev, payload: { ...prev?.payload, tab: value } };
                });
              }}
              options={[
                { value: 'product', label: '상품' },
                { value: 'partner', label: '파트너' },
                { value: 'user-content', label: '라운지' },
              ]}></Select>
          </div>
        )}
        {action === DeepLinkActions.GO_TO_PARTNER && (
          <div style={{ marginTop: '8px', display: 'flex', gap: '4px', flexDirection: 'column' }}>
            <SelectUser
              style={{ width: '528px' }}
              onSelect={(partner) => {
                setChangedActionData((prev) => {
                  return { ...prev, payload: { ...prev?.payload, partnerId: partner._id } } as any;
                });
              }}></SelectUser>
          </div>
        )}
        {action === DeepLinkActions.GO_TO_NOTICE && (noticeSearchResult.length > 0 || payload?.noticeId) && (
          <Select
            style={{ width: '100%' }}
            value={payload?.noticeId}
            onChange={(value) => {
              setChangedActionData((prev) => {
                return { ...prev, payload: { ...prev?.payload, noticeId: value } };
              });
            }}
            options={noticeSearchResultOptions}
          />
        )}
      </div>
    </div>
  );
};

export default DeeplinkActionEdit;
